import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import React from 'react';
import { Link } from 'react-router-dom';

type Target =
  | {
      action: (event: React.MouseEvent<HTMLButtonElement>) => void;
      disabled?: boolean;
      to?: never;
    }
  | { action?: never; disabled?: never; to: string };

type Props = Target & {
  dataTestId?: string;
  dataUi?: string;
  linkColor?: string;
  sx?: SxProps;
  title: string;
  underline?: boolean;
  variant?: 'action';
};
const PseudoLink = ({
  action,
  dataTestId,
  dataUi,
  disabled = false,
  linkColor,
  sx = {},
  title,
  to,
  underline = true,
  variant,
}: Props) => {
  const theme = useTheme();
  const resolvedLinkColor = linkColor ?? theme.palette.info.main;
  const isAction = variant === 'action';
  const underlinesOnHover = isAction && !underline;
  const resetSx = {
    background: 'none',
    border: 0,
    color: 'inherit',
    display: 'inherit',
    font: 'inherit',
    letterSpacing: 'inherit',
    overflow: 'inherit',
    p: 0,
    textAlign: 'inherit',
    textDecoration: 'none',
    textOverflow: 'inherit',
    textTransform: 'inherit',
    width: '100%',
  };
  const label = (
    <Typography
      component={'span'}
      variant={'body2'}
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...(isAction && { fontWeight: 500, lineHeight: 'inherit' }),
        textDecoration: disabled ? 'none' : underline ? 'underline' : 'none',
        color: disabled ? theme.palette.text.disabled : resolvedLinkColor,
        '&:hover': {
          textDecoration: underlinesOnHover ? 'underline' : 'none',
          textDecorationStyle: 'none',
        },
      }}
    >
      {title}
    </Typography>
  );
  return (
    <Typography
      component="span"
      variant={isAction ? 'body2' : undefined}
      sx={{
        display: 'block',
        // normalize.css gives buttons line-height 1.15; action links keep that compact height
        ...(isAction && { lineHeight: 1.15 }),
        ...sx,
      }}
    >
      {to ? (
        <Box
          component={Link}
          data-test-id={dataTestId}
          data-ui={dataUi}
          sx={resetSx}
          to={to}
        >
          {label}
        </Box>
      ) : (
        <Box
          component="button"
          data-test-id={dataTestId}
          data-ui={dataUi}
          disabled={disabled}
          onClick={action}
          sx={resetSx}
          type="button"
        >
          {label}
        </Box>
      )}
    </Typography>
  );
};

export { PseudoLink };
